import React, { useMemo } from 'react';
import { formatBillingCurrency } from '../../../utils/billingRound';
import type { AzureService3MonthAnalysisRow } from '../../../types/azureCost';

const HEADER_BG = 'bg-[#bdd7ee]';
const TOTAL_BG = 'bg-[#bdd7ee]';
const INCREASED_BG = 'bg-[#fce4b2]';
const DECREASED_BG = 'bg-[#c6efce]';

function rowClassName(trend: AzureService3MonthAnalysisRow['trend']): string {
  if (trend === 'increased') return INCREASED_BG;
  if (trend === 'decreased') return DECREASED_BG;
  return 'bg-white';
}

function formatMeterCount(value: number): string {
  return value.toLocaleString('en-US');
}

export interface AzureCost3MonthAnalysisTableProps {
  rows: AzureService3MonthAnalysisRow[];
  currency: string;
  className?: string;
}

const AzureCost3MonthAnalysisTable: React.FC<AzureCost3MonthAnalysisTableProps> = ({
  rows,
  currency,
  className = '',
}) => {
  const monthLabels = useMemo(() => {
    if (rows.length === 0) return [];
    return rows[0]!.months.map((m) => m.label);
  }, [rows]);

  const totals = useMemo(() => {
    if (rows.length === 0) return null;
    const monthCount = rows[0]!.months.length;
    const months = Array.from({ length: monthCount }, (_, idx) => ({
      label: rows[0]!.months[idx]!.label,
      meterCount: 0,
      cost: 0,
    }));
    for (const row of rows) {
      row.months.forEach((m, idx) => {
        months[idx]!.meterCount += m.meterCount;
        months[idx]!.cost += m.cost;
      });
    }
    const prev = months[monthCount - 2]?.cost ?? 0;
    const curr = months[monthCount - 1]?.cost ?? 0;
    return { months, costDiff: curr - prev };
  }, [rows]);

  if (rows.length === 0 || monthLabels.length === 0) {
    return (
      <div className={`rounded-xl border border-slate-200/80 bg-white p-6 text-center text-sm text-slate-500 ${className}`}>
        No Azure service analysis data for the last 3 months.
      </div>
    );
  }

  const cellBorder = 'border border-slate-400/80 px-2 py-1.5 text-center text-xs text-slate-900';
  const headerCell = `${cellBorder} font-semibold ${HEADER_BG}`;

  return (
    <div
      id="msr-azure-cost-3months-analysis-table"
      className={`overflow-x-auto rounded-xl border border-slate-300/90 bg-white shadow-sm ${className}`}
    >
      <table className="min-w-full border-collapse text-xs">
        <thead>
          <tr>
            <th rowSpan={2} className={`${headerCell} min-w-[140px] text-left align-middle`}>
              <div>Month</div>
              <div className="mt-1 font-semibold">Services</div>
            </th>
            {monthLabels.map((label) => (
              <th key={label} colSpan={2} className={headerCell}>
                {label}
              </th>
            ))}
            <th rowSpan={2} className={`${headerCell} min-w-[88px] align-middle`}>
              Cost Diff.
            </th>
          </tr>
          <tr>
            {monthLabels.map((label) => (
              <React.Fragment key={`${label}-sub`}>
                <th className={headerCell}>Meter Count of Resource ID</th>
                <th className={headerCell}>Sum of Cost USD</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.serviceName} className={rowClassName(row.trend)}>
              <td className={`${cellBorder} text-left font-medium`}>{row.serviceName}</td>
              {row.months.map((m) => (
                <React.Fragment key={`${row.serviceName}-${m.label}`}>
                  <td className={cellBorder}>{formatMeterCount(m.meterCount)}</td>
                  <td className={cellBorder}>{formatBillingCurrency(m.cost, currency)}</td>
                </React.Fragment>
              ))}
              <td className={`${cellBorder} font-semibold`}>
                {formatBillingCurrency(row.costDiff, currency)}
              </td>
            </tr>
          ))}
          {totals ? (
            <tr className={TOTAL_BG}>
              <td className={`${cellBorder} text-left font-bold`}>Grand Total</td>
              {totals.months.map((m) => (
                <React.Fragment key={`total-${m.label}`}>
                  <td className={`${cellBorder} font-bold`}>{formatMeterCount(m.meterCount)}</td>
                  <td className={`${cellBorder} font-bold`}>
                    {formatBillingCurrency(m.cost, currency)}
                  </td>
                </React.Fragment>
              ))}
              <td className={`${cellBorder} font-bold`}>
                {formatBillingCurrency(totals.costDiff, currency)}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
};

export default AzureCost3MonthAnalysisTable;
